import { useCallback, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { DialogTrigger } from "react-aria-components";
import {
  MDSText,
  MDSTextVariant,
  MDSIconKind,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSButton,
  MDSIcon,
} from "@/mds";
import { MDSSelectMenu, MDSSelectMenuItem, MDSMenuDropdownPopover } from "@/mds/components/menu";
import {
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
} from "@/mds/components/menu/menu-item/menu-item.types";
import { ThemeType } from "@/mds/themes/theme-context";
import { THEME_ICON, THEME_LABEL } from "@/mds/themes/theme-constants";
import { useThemeSelection } from "@/domains/theme/use-theme-selection";
import { type Selection } from "react-aria-components";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";

export const SettingsAppearancePage = observer(() => {
  const { themePreference, selectTheme } = useThemeSelection();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = useCallback(
    (keys: Selection) => {
      if (keys === "all") return;
      const selectedId = [...keys][0] as string;
      if (!selectedId) return;

      selectTheme(selectedId);
      setIsOpen(false);
    },
    [selectTheme]
  );

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="General" title="Appearance" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledThemeRow>
            <MDSText variant={MDSTextVariant.TextS}>Theme</MDSText>
            <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
              <MDSButton variant={MDSButtonVariant.Default}>
                <MDSIcon
                  slot={MDSButtonSlotName.LeftIcon}
                  kind={THEME_ICON[themePreference]}
                  size={14}
                />
                {THEME_LABEL[themePreference]}
                <MDSIcon
                  slot={MDSButtonSlotName.RightIcon}
                  kind={MDSIconKind.AngleDown}
                  size={12}
                />
              </MDSButton>
              <MDSMenuDropdownPopover placement="bottom end" style={{ width: 176 }}>
                <MDSSelectMenu
                  aria-label="Select theme"
                  selectionMode="single"
                  selectedKeys={new Set([themePreference])}
                  onSelectionChange={handleSelect}
                >
                  <MDSSelectMenuItem
                    id={ThemeType.Harvest}
                    label="Light"
                    icon={MDSIconKind.SunBright}
                    isSelected={themePreference === ThemeType.Harvest}
                    selectionVariant={
                      themePreference === ThemeType.Harvest
                        ? MDSMenuItemSelectionVariant.Checkmark
                        : undefined
                    }
                    variant={MDSMenuItemVariant.OneLine}
                  />
                  <MDSSelectMenuItem
                    id={ThemeType.HarvestMoon}
                    label="Dark"
                    icon={MDSIconKind.Moon}
                    isSelected={themePreference === ThemeType.HarvestMoon}
                    selectionVariant={
                      themePreference === ThemeType.HarvestMoon
                        ? MDSMenuItemSelectionVariant.Checkmark
                        : undefined
                    }
                    variant={MDSMenuItemVariant.OneLine}
                  />
                  <MDSSelectMenuItem
                    id={ThemeType.System}
                    label="System"
                    icon={MDSIconKind.Desktop}
                    isSelected={themePreference === ThemeType.System}
                    selectionVariant={
                      themePreference === ThemeType.System
                        ? MDSMenuItemSelectionVariant.Checkmark
                        : undefined
                    }
                    variant={MDSMenuItemVariant.OneLine}
                  />
                </MDSSelectMenu>
              </MDSMenuDropdownPopover>
            </DialogTrigger>
          </StyledThemeRow>
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledThemeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 700px;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;
